/**
 * Valida um CPF brasileiro
 * @param cpf - CPF a ser validado (com ou sem formatação)
 * @returns true se o CPF for válido, false caso contrário
 */
export function validateCPF(cpf: string): boolean {
  // Remove todos os caracteres não numéricos
  cpf = cpf.replace(/[^\d]/g, "");
  
  // Verifica se tem exatamente 11 dígitos
  if (cpf.length !== 11) return false;
  
  // Verifica se todos os dígitos são iguais (CPF inválido)
  if (/^(\d)\1+$/.test(cpf)) return false;
  
  // Validação do primeiro dígito verificador
  let soma = 0;
  for (let i = 0; i < 9; i++) {
    soma += parseInt(cpf.charAt(i)) * (10 - i);
  }
  let resto = 11 - (soma % 11);
  let dv1 = resto > 9 ? 0 : resto;
  
  if (dv1 !== parseInt(cpf.charAt(9))) return false;
  
  // Validação do segundo dígito verificador
  soma = 0;
  for (let i = 0; i < 10; i++) {
    soma += parseInt(cpf.charAt(i)) * (11 - i);
  }
  resto = 11 - (soma % 11);
  let dv2 = resto > 9 ? 0 : resto;
  
  if (dv2 !== parseInt(cpf.charAt(10))) return false;
  
  return true;
}

/**
 * Valida um CNPJ brasileiro
 * @param cnpj - CNPJ a ser validado (com ou sem formatação)
 * @returns true se o CNPJ for válido, false caso contrário
 */
export function validateCNPJ(cnpj: string): boolean {
  // Remove todos os caracteres não numéricos
  cnpj = cnpj.replace(/[^\d]/g, "");
  
  // Verifica se tem exatamente 14 dígitos
  if (cnpj.length !== 14) return false;
  
  // Verifica se todos os dígitos são iguais (CNPJ inválido)
  if (/^(\d)\1+$/.test(cnpj)) return false;
  
  // Validação do primeiro dígito verificador
  let tamanho = cnpj.length - 2;
  let numeros = cnpj.substring(0, tamanho);
  const digitos = cnpj.substring(tamanho);
  let soma = 0;
  let pos = tamanho - 7;
  
  for (let i = tamanho; i >= 1; i--) {
    soma += parseInt(numeros.charAt(tamanho - i)) * pos--;
    if (pos < 2) pos = 9;
  }
  
  let resultado = soma % 11 < 2 ? 0 : 11 - (soma % 11);
  if (resultado !== parseInt(digitos.charAt(0))) return false;
  
  // Validação do segundo dígito verificador
  tamanho = tamanho + 1;
  numeros = cnpj.substring(0, tamanho);
  soma = 0;
  pos = tamanho - 7;
  
  for (let i = tamanho; i >= 1; i--) {
    soma += parseInt(numeros.charAt(tamanho - i)) * pos--;
    if (pos < 2) pos = 9;
  }
  
  resultado = soma % 11 < 2 ? 0 : 11 - (soma % 11);
  if (resultado !== parseInt(digitos.charAt(1))) return false;
  
  return true;
}

/**
 * Valida um CPF ou CNPJ baseado no tamanho
 * @param value - CPF ou CNPJ a ser validado (com ou sem formatação)
 * @returns true se o CPF ou CNPJ for válido, false caso contrário
 */
export function validateCPFOrCNPJ(value: string): boolean {
  if (!value) return false;
  
  // Remove formatação
  const cleaned = value.replace(/\D/g, '');
  
  // Verifica o tamanho para determinar se é CPF (11) ou CNPJ (14)
  if (cleaned.length === 11) {
    return validateCPF(cleaned);
  } else if (cleaned.length === 14) {
    return validateCNPJ(cleaned);
  }
  
  return false;
}

/**
 * Valida uma data no formato dd/mm/yyyy
 * @param dateString - Data no formato dd/mm/yyyy
 * @returns true se a data for válida, false caso contrário
 */
export function validateDateString(dateString: string): boolean {
  if (!dateString || dateString.length !== 10) return false;
  
  const parts = dateString.split('/');
  if (parts.length !== 3) return false;
  
  const day = parseInt(parts[0]);
  const month = parseInt(parts[1]);
  const year = parseInt(parts[2]);
  
  if (isNaN(day) || isNaN(month) || isNaN(year)) return false;
  
  const date = new Date(year, month - 1, day);
  
  // Verifica se a data é válida e se os componentes correspondem
  return date.getDate() === day && 
         date.getMonth() === month - 1 && 
         date.getFullYear() === year;
}

// Função de teste para verificar CPFs específicos
export function testCPFValidation() {
  const testCPFs = [
    "073.182.813-56", // CPF da imagem
    "111.111.111-11", // CPF inválido (todos iguais)
    "123.456.789-01", // CPF inválido (sequência)
    "000.000.001-91", // CPF válido
  ];
  
  testCPFs.forEach(cpf => {
    const isValid = validateCPF(cpf);
    console.log(`CPF ${cpf}: ${isValid ? 'VÁLIDO' : 'INVÁLIDO'}`);
  });
}

// Função de teste para verificar datas específicas
export function testDateValidation() {
  const testDates = [
    "17/04/1995", // Data de nascimento da imagem
    "05/04/2016", // Data de emissão do RG da imagem
    "32/13/2023", // Data inválida
    "00/00/0000", // Data inválida
  ];
  
  testDates.forEach(date => {
    const isValid = validateDateString(date);
    console.log(`Data ${date}: ${isValid ? 'VÁLIDA' : 'INVÁLIDA'}`);
  });
} 